"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Menu, X, Phone, Mail, ChevronDown, GraduationCap, UserCircle, Briefcase, Globe } from "lucide-react"
import Image from "next/image"
import { CareerFormModal } from "@/components/career-form-modal"

// Declare window types for Google Translate
declare global {
  interface Window {
    googleTranslateElementInit: () => void
    google: any
  }
}

export function MVHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [fontSize, setFontSize] = useState(100)
  const [selectedLanguage, setSelectedLanguage] = useState("en")
  const [isCareerModalOpen, setIsCareerModalOpen] = useState(false)

  const languages = [
    { code: "en", name: "English", nativeName: "English" },
    { code: "hi", name: "Hindi", nativeName: "हिंदी" },
    { code: "es", name: "Spanish", nativeName: "Español" },
    { code: "fr", name: "French", nativeName: "Français" },
    { code: "de", name: "German", nativeName: "Deutsch" },
    { code: "zh", name: "Chinese", nativeName: "中文" },
    { code: "ja", name: "Japanese", nativeName: "日本語" },
    { code: "ar", name: "Arabic", nativeName: "العربية" },
    { code: "pt", name: "Portuguese", nativeName: "Português" },
    { code: "ru", name: "Russian", nativeName: "Русский" },
  ]

  const toggleDropdown = (id: string) => {
    setActiveDropdown((prev) => (prev === id ? null : id))
  }

  const increaseFontSize = () => {
    if (fontSize < 150) {
      setFontSize(fontSize + 10)
      document.documentElement.style.fontSize = `${fontSize + 10}%`
    }
  }

  const decreaseFontSize = () => {
    if (fontSize > 70) {
      setFontSize(fontSize - 10)
      document.documentElement.style.fontSize = `${fontSize - 10}%`
    }
  }

  // Initialize Google Translate
  useEffect(() => {
    const addGoogleTranslateScript = () => {
      if (document.getElementById('google-translate-script')) return

      const script = document.createElement('script')
      script.id = 'google-translate-script'
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
      script.async = true
      document.body.appendChild(script)

      window.googleTranslateElementInit = () => {
        if (window.google && window.google.translate) {
          new window.google.translate.TranslateElement(
            {
              pageLanguage: 'en',
              includedLanguages: 'en,hi,es,fr,de,zh-CN,ja,ar,pt,ru',
              layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
              autoDisplay: false,
            },
            'google_translate_element'
          )
        }
      }
    }

    addGoogleTranslateScript()

    return () => {
      const script = document.getElementById('google-translate-script')
      if (script) {
        document.body.removeChild(script)
      }
    }
  }, [])

  const handleLanguageChange = (langCode: string) => {
    setSelectedLanguage(langCode)
    
    // Trigger Google Translate
    const googleTranslateSelect = document.querySelector('.goog-te-combo') as HTMLSelectElement
    if (googleTranslateSelect) {
      const languageMap: { [key: string]: string } = {
        'en': 'en',
        'hi': 'hi',
        'es': 'es',
        'fr': 'fr',
        'de': 'de',
        'zh': 'zh-CN',
        'ja': 'ja',
        'ar': 'ar',
        'pt': 'pt',
        'ru': 'ru',
      }
      
      googleTranslateSelect.value = languageMap[langCode] || langCode
      googleTranslateSelect.dispatchEvent(new Event('change'))
    }
  }

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      {/* Hidden Google Translate Element */}
      <div id="google_translate_element" className="hidden"></div>

      {/* Desktop Top Bar */}
      <div className="bg-gradient-to-r from-[#0a2647] via-[#144272] to-[#205295] text-white py-2 hidden lg:block">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Contact Information */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Phone className="h-3.5 w-3.5" />
                <span className="text-xs">+91 99876 54321</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5" />
                <span className="text-xs">info@mvtutorials.edu</span>
              </div>
            </div>
            
            {/* Center: Three Action Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* <a 
                href="/mv-tutorials/alumni" 
                className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 rounded-md transition-all text-xs font-semibold border-2 border-white shadow-md hover:shadow-lg no-underline text-slate-700"
                aria-label="Alumni"
              >
                <GraduationCap className="h-4 w-4" />
                <span>Alumni</span>
              </a> */}
              <a 
                href="#student-panel" 
                className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 rounded-md transition-all text-xs font-semibold border-2 border-white shadow-md hover:shadow-lg no-underline text-slate-700"
                aria-label="Student Panel"
              >
                <UserCircle className="h-4 w-4" />
                <span>Student Panel</span>
              </a>
              <button 
                onClick={() => setIsCareerModalOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 rounded-md transition-all text-xs font-semibold border-2 border-white shadow-md hover:shadow-lg text-slate-700"
                aria-label="Career"
              >
                <Briefcase className="h-4 w-4" />
                <span>Career</span>
              </button>
            </div>
            
            {/* Right: Zoom Buttons and Language Selector */}
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Zoom Buttons */}
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={decreaseFontSize} 
                  className="h-7 px-2 flex items-center justify-center rounded bg-white text-slate-700 hover:bg-gray-50 transition-all text-xs font-semibold"
                  aria-label="Decrease text size"
                >
                  A-
                </button>
                <button 
                  onClick={increaseFontSize} 
                  className="h-7 px-2 flex items-center justify-center rounded bg-white text-slate-700 hover:bg-gray-50 transition-all text-xs font-semibold"
                  aria-label="Increase text size"
                >
                  A+
                </button>
              </div>
              
              {/* Language Selector */}
              <div className="relative group">
                <button className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded text-slate-700 hover:bg-gray-50 transition-all text-xs font-medium">
                  <Globe className="h-3.5 w-3.5" />
                  <span>{languages.find(l => l.code === selectedLanguage)?.nativeName || "English"}</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
                <div className="absolute top-full right-0 mt-1 bg-white shadow-lg rounded-md py-2 w-44 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 max-h-64 overflow-y-auto">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        selectedLanguage === lang.code
                          ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {lang.nativeName}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-1 lg:px-2">
        <div className="flex items-center justify-between py-3 lg:py-4 gap-4 lg:gap-6">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Image
              src="/images/verma.webp"
              alt="MV Tutorials Logo"
              width={70}
              height={70}
              className="w-[50px] h-[50px] lg:w-[70px] lg:h-[70px] rounded-full border-2 border-blue-100 shadow-lg"
            />
            <div className="min-w-0">
              <h1 className="text-lg lg:text-2xl xl:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
                M.V. Tutorials
              </h1>
              <p className="text-xs lg:text-sm xl:text-base text-slate-500 font-semibold leading-tight">
                Verma Group of Institution
              </p>
            </div>
          </div>

          <nav className="hidden xl:flex items-center gap-3">
            <a href="/" className="px-2 py-1 text-slate-700 hover:text-blue-700 font-medium text-sm 2xl:text-base">
              Home
            </a>
            <a href="/mv-tutorials/about" className="px-2 py-1 text-slate-700 hover:text-blue-700 font-medium text-sm 2xl:text-base">
              About Us
            </a>
            <div className="relative group">
              <button className="px-2 py-1 flex items-center gap-1 text-slate-700 hover:text-blue-700 font-medium text-sm 2xl:text-base">
                Management
                <ChevronDown className="h-3 w-3 2xl:h-4 2xl:w-4" />
              </button>
              <div className="absolute top-full left-0 bg-white shadow-lg rounded-md py-2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <a href="/mv-tutorials/managing-director" className="block px-4 py-2 text-slate-700 hover:bg-blue-50 hover:text-blue-700">
                  Managing Director
                </a>
                <a href="/mv-tutorials/principal" className="block px-4 py-2 text-slate-700 hover:bg-blue-50 hover:text-blue-700">
                  Principal
                </a>
              </div>
            </div>
            {/* <a href="/mv-tutorials/alumni" className="px-2 py-1 text-slate-700 hover:text-blue-700 font-medium text-sm 2xl:text-base">
              Alumni
            </a> */}
            <a href="/mv-tutorials/infrastructure" className="px-2 py-1 text-slate-700 hover:text-blue-700 font-medium text-sm 2xl:text-base">
              Infrastructure
            </a>
            <a href="/mv-tutorials/gallery" className="px-2 py-1 text-slate-700 hover:text-blue-700 font-medium text-sm 2xl:text-base">
              Gallery
            </a>
            <a href="/mv-tutorials/contact" className="px-2 py-1 text-slate-700 hover:text-blue-700 font-medium text-sm 2xl:text-base">
              Contact
            </a>
          </nav>

          <Button variant="ghost" size="sm" className="xl:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {isMenuOpen && (
          <nav className="xl:hidden py-4 border-t bg-white">
            <div className="flex flex-col gap-1">
              {/* Quick Action Buttons */}
              <div className="px-4 mb-4">
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <a 
                    href="/mv-tutorials/alumni" 
                    className="flex flex-col items-center justify-center gap-2 p-3 bg-gradient-to-br from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 rounded-lg transition-all shadow-md hover:shadow-lg no-underline text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <GraduationCap className="h-6 w-6" />
                    <span className="text-xs font-semibold">Alumni</span>
                  </a>
                  <a 
                    href="#student-panel" 
                    className="flex flex-col items-center justify-center gap-2 p-3 bg-gradient-to-br from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 rounded-lg transition-all shadow-md hover:shadow-lg no-underline text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <UserCircle className="h-6 w-6" />
                    <span className="text-xs font-semibold">Student Panel</span>
                  </a>
                  <button 
                    onClick={() => {
                      setIsMenuOpen(false)
                      setIsCareerModalOpen(true)
                    }}
                    className="flex flex-col items-center justify-center gap-2 p-3 bg-gradient-to-br from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 rounded-lg transition-all shadow-md hover:shadow-lg text-white w-full"
                  >
                    <Briefcase className="h-6 w-6" />
                    <span className="text-xs font-semibold">Career</span>
                  </button>
                </div>
                
                {/* Language Selector */}
                <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                  <Globe className="h-4 w-4 text-blue-700 flex-shrink-0" />
                  <select 
                    value={selectedLanguage}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    className="flex-1 bg-white rounded border border-gray-300 text-sm font-medium text-gray-700 px-3 py-2 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23374151'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.25rem 1.25rem',
                      paddingRight: '2.5rem',
                    }}
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.nativeName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Contact details */}
              <div className="bg-gradient-to-r from-[#0a2647] via-[#144272] to-[#205295] text-white p-4 rounded-md mb-4 mx-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span className="text-sm">+91 99876 54321</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span className="text-sm">info@mvtutorials.edu</span>
                  </div>
                </div>
              </div>

              <a href="/" className="py-3 px-4 text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-all">
                Home
              </a>
              <a href="/mv-tutorials/about" className="py-3 px-4 text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-all">
                About Us
              </a>
              <div className="border-b border-gray-100">
                <button
                  onClick={() => toggleDropdown("management")}
                  className="w-full flex items-center justify-between py-3 px-4 text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-all"
                >
                  Management
                  <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === "management" ? "rotate-180" : ""}`} />
                </button>
                {activeDropdown === "management" && (
                  <div className="pl-4 pb-2 space-y-1">
                    <a href="/mv-tutorials/managing-director" className="block py-2 px-4 text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-700 rounded-md">
                      Managing Director
                    </a>
                    <a href="/mv-tutorials/principal" className="block py-2 px-4 text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-700 rounded-md">
                      Principal
                    </a>
                  </div>
                )}
              </div>
              {/* <a href="/mv-tutorials/alumni" className="py-3 px-4 text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-all">
                Alumni
              </a> */}
              <a href="/mv-tutorials/infrastructure" className="py-3 px-4 text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-all">
                Infrastructure
              </a>
              <a href="/mv-tutorials/gallery" className="py-3 px-4 text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-all">
                Gallery
              </a>
              <a href="/mv-tutorials/contact" className="py-3 px-4 text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-all">
                Contact
              </a>
            </div>
          </nav>
        )}
      </div>

      {/* Career Form Modal */}
      <CareerFormModal isOpen={isCareerModalOpen} onClose={() => setIsCareerModalOpen(false)} />
    </header>
  )
}
